import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react'
import { errorMessage } from '../../chat/api'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useToast } from '../../library/useToast'
import { useCrmRequest } from '../CrmRequest'
import { submitWithdrawal, type Balances, type WithdrawOutcome } from '../statements/statementsData'
import { WithdrawModal } from '../statements/WithdrawModal'
import {
  ChargebacksSide,
  FansSide,
  MessagesSide,
  OverviewSide,
  PostsSide,
  PromotionsSide,
  ReferralsSide,
  StatementsSide,
  StoriesSide,
  StreamingSide,
  TrackingSide,
  TrialsSide,
  VisitorsSide,
} from './Aside'
import { MessagesMain, PostsMain, StoriesMain, StreamingMain } from './EngagementViews'
import { ActivityMain, SubscriptionsMain, TopFansMain } from './FansViews'
import { HighlightsMain, OverviewEarningsMain, StreakMain } from './OverviewViews'
import { CustomRange, PeriodBar } from './PeriodBar'
import { PromotionsMain, TrackingMain, TrialsMain, VisitorsMain } from './ReachViews'
import { ChargebacksMain, EarningsMain, PayoutsMain, ReferralsMain } from './StatementsViews'
import { StatsContext, useStatsT, type StatsCtx } from './statsContext'
import { Pills } from './StatsParts'
import {
  computeRange,
  currentOfUrl,
  customFromRange,
  DEFAULT_PILLS,
  DEFAULT_SUBS,
  viewFromQuery,
  periodKey,
  TABS,
  type Month,
  type Period,
  type Pills as PillState,
  type Query,
  type Range,
  type Raw,
  type TabId,
} from './statisticsData'
import { statKey, StatsStore } from './statsStore'
import '../../library/library.css'
import '../ofPages.css'
import '../statements/statements.css'
import './statistics.css'

interface Props {
  accountId: string
  /** Navigates to another CRM page of this account (Back goes to 'home'). */
  onOpenPage: (page: string) => void
  /** Opens the fan's chat on the Chats page. */
  onOpenChat: (fanId: number | string) => void
  /** page.statements write: changing the payout frequency and requesting withdrawals. */
  canWritePayouts: boolean
  mobileNav?: ReactNode
  /** The page's URL query: `view=<tab>/<sub>` opens that tab (e.g. `reach/promotions` from Settings). */
  query?: string
}

interface View {
  Main: ComponentType
  Side: ComponentType
  /** false: no period dropdown (Activity Streak has its own months). */
  period?: false
  /** false: the dropdown shows no "(local time …)" (Statements). */
  tz?: false
}

/** Every tab/sub-tab of OnlyFans' statistics and what it shows (the CRM's STX_VIEWS). */
const VIEWS: Record<string, View> = {
  'statements/earnings': { Main: EarningsMain, Side: StatementsSide, tz: false },
  'statements/payouts': { Main: PayoutsMain, Side: StatementsSide, tz: false },
  'statements/chargebacks': { Main: ChargebacksMain, Side: ChargebacksSide, tz: false },
  'statements/referrals': { Main: ReferralsMain, Side: ReferralsSide, tz: false },
  'overview/earnings': { Main: OverviewEarningsMain, Side: OverviewSide },
  'overview/highlights': { Main: HighlightsMain, Side: OverviewSide },
  'overview/streak': { Main: StreakMain, Side: OverviewSide, period: false },
  'engagement/posts': { Main: PostsMain, Side: PostsSide },
  'engagement/messages': { Main: MessagesMain, Side: MessagesSide },
  'engagement/streaming': { Main: StreamingMain, Side: StreamingSide },
  'engagement/stories': { Main: StoriesMain, Side: StoriesSide },
  'reach/visitors': { Main: VisitorsMain, Side: VisitorsSide },
  'reach/promotions': { Main: PromotionsMain, Side: PromotionsSide },
  'reach/trials': { Main: TrialsMain, Side: TrialsSide },
  'reach/tracking': { Main: TrackingMain, Side: TrackingSide },
  'fans/subscriptions': { Main: SubscriptionsMain, Side: FansSide },
  'fans/top': { Main: TopFansMain, Side: FansSide },
  'fans/activity': { Main: ActivityMain, Side: FansSide },
}

/**
 * Statistics: a copy of onlyfans.com/my/statistics (the CRM's statistics page),
 * every number read through the CRM's allow-listed POST /v1/statistics/of.
 * Calls are cached per (path, query) for the page's lifetime, so going back
 * to a tab or a period is instant; each view fires its chart, list and
 * summary calls together.
 */
export function StatisticsPage({ accountId, onOpenPage, onOpenChat, canWritePayouts, mobileNav, query }: Props) {
  const { t } = useStatsT()
  const request = useCrmRequest()
  const mobile = useIsNarrow()
  const { say, node: toast } = useToast()
  const requestRef = useRef(request)
  requestRef.current = request

  const [store] = useState(
    () =>
      new StatsStore(async (path: string, query: Query) => {
        const { status, data } = await requestRef.current('/v1/statistics/of', { local: true, body: { path, query } })
        const body = data as Raw | null
        if (status !== 200 || !body || body.ok === false) throw new Error(errorMessage(body, `HTTP ${status}`))
        return body.data
      }),
  )
  const [openedAt] = useState(() => new Date())
  const ranges = useRef(new Map<string, Range>())
  const periodGen = useRef(0)

  const asked = viewFromQuery(query)
  const [tab, setTab] = useState<TabId>(asked?.tab ?? 'statements')
  const [subs, setSubs] = useState<Record<TabId, string>>(() => (asked ? { ...DEFAULT_SUBS, [asked.tab]: asked.sub } : DEFAULT_SUBS))
  const [pills, setPills] = useState<PillState>(DEFAULT_PILLS)
  const [period, setPeriod] = useState<Period>({ key: '30d' })
  const [range, setRange] = useState<Range>(() => {
    const r = computeRange({ key: '30d' }, null, openedAt)
    ranges.current.set('30d', r)
    return r
  })
  const [menuOpen, setMenuOpen] = useState(false)
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null)
  const [streakMonth, setStreakMonth] = useState<Month>(() => ({ y: openedAt.getFullYear(), m: openedAt.getMonth() }))
  const [countriesAll, setCountriesAll] = useState(false)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [withdrawing, setWithdrawing] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const sub = subs[tab]
  const viewId = `${tab}/${sub}`
  const view = VIEWS[viewId] ?? VIEWS['statements/earnings']!
  const tabDef = TABS.find((x) => x.id === tab) ?? TABS[0]!

  const scrollTop = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }
  const pickTab = (id: TabId) => {
    setTab(id)
    setMenuOpen(false)
    scrollTop()
  }
  const pickSub = (id: string) => {
    setSubs((all) => ({ ...all, [tab]: id }))
    setMenuOpen(false)
    scrollTop()
  }
  const setPill = useCallback((group: keyof PillState, value: string) => setPills((all) => ({ ...all, [group]: value })), [])

  /** One range per period choice for the page's lifetime: going back to a period hits the cache. */
  const applyPeriod = async (next: Period) => {
    const gen = ++periodGen.current
    const key = periodKey(next)
    let r = ranges.current.get(key)
    if (!r) {
      let startModel: string | null = null
      if (next.key === 'all') {
        try {
          startModel = String(((await store.read('users/me/start-date-model')) as Raw | null)?.startDate || '') || null
        } catch {
          startModel = null
        }
      }
      if (gen !== periodGen.current) return
      r = computeRange(next, startModel)
      ranges.current.set(key, r)
    }
    setPeriod(next)
    setRange(r)
    setCountriesAll(false)
    setExpanded(new Set())
  }
  const pickPeriod = (key: string) => {
    setMenuOpen(false)
    if (key === 'custom') setCustom(customFromRange(range))
    else void applyPeriod({ key })
  }
  const applyCustom = () => {
    if (!custom || !custom.from || !custom.to || custom.from > custom.to) return
    setCustom(null)
    void applyPeriod({ key: 'custom', from: custom.from, to: custom.to })
  }

  // The dropdown closes on any outside click, Escape closes the top-most layer.
  useEffect(() => {
    if (!menuOpen) return
    const close = () => setMenuOpen(false)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [menuOpen])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (custom) setCustom(null)
      else if (menuOpen) setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [custom, menuOpen])

  const ctx: StatsCtx = {
    store,
    request,
    range,
    pills,
    setPill,
    expanded,
    toggleExpanded: (id) =>
      setExpanded((all) => {
        const next = new Set(all)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      }),
    countriesAll,
    showAllCountries: () => setCountriesAll(true),
    streakMonth,
    setStreakMonth,
    openChat: (fanId) => onOpenChat(fanId),
    openWithdrawal: () => setWithdrawing(true),
    canWritePayouts,
    say,
    openedAt,
  }

  // Withdrawals: the Statements page's modal and its outcome rules (statementsData.submitWithdrawal).
  const submit = (amount: number, key: string) => submitWithdrawal(request, accountId, amount, key)
  // Taken (or lost on the way): balances, the payouts chart and the payout requests are read again.
  const withdrawalFinished = (outcome: WithdrawOutcome) => {
    setWithdrawing(false)
    if (outcome.kind === 'ok') {
      say(
        outcome.state === 'approved'
          ? t('payoutProcessed')
          : outcome.state === 'rejected'
            ? [t('payoutReturned'), outcome.rejectReason && t('rejectReason', { reason: outcome.rejectReason })].filter(Boolean).join(' ')
            : t('payoutProcessing'),
      )
    } else say(t('withdrawUnknown'))
    store.invalidate(
      (key) =>
        key.startsWith('payouts/balances?') || key.startsWith('payouts/check-receive?') || key.startsWith('payouts/chart?') || key.startsWith('payouts/requests|'),
    )
  }
  const balances = store.get(statKey('payouts/balances'))?.data as Balances | null | undefined

  const { Main, Side } = view
  const head = (
    <header className="lib-main-head ofx-head">
      <button type="button" className="lib-icon-btn" aria-label={t('back')} title={t('back')} onClick={() => onOpenPage('home')}>
        <FeatherIcon name="arrow-left" size={19} />
      </button>
      <h1 className="lib-main-title ofx-title">{t('title')}</h1>
      <span className="lib-spacer" />
      <a
        className="lib-icon-btn"
        href={currentOfUrl(tab, sub, pills)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('openOnOf')}
        title={t('openOnOf')}
      >
        <FeatherIcon name="external-link" size={18} />
      </a>
    </header>
  )
  const tabs = (
    <nav className="ofx-tabs" role="tablist" aria-label={t('sections')}>
      {TABS.map((x) => (
        <button key={x.id} type="button" role="tab" aria-selected={x.id === tab} className={x.id === tab ? 'is-on' : ''} onClick={() => pickTab(x.id)}>
          {t(`tab.${x.id}`)}
        </button>
      ))}
    </nav>
  )
  const content = (
    <>
      <div className="ofx-subtabs">
        <Pills items={tabDef.subs.map((s) => [s.id, s.label] as const)} active={sub} onPick={pickSub} label={t(`tab.${tab}`)} />
      </div>
      {view.period !== false && (
        <PeriodBar period={period} range={range} withTz={view.tz !== false} open={menuOpen} onToggle={() => setMenuOpen((o) => !o)} onPick={pickPeriod} />
      )}
      <div className="ofx-content">
        <Main key={viewId} />
      </div>
    </>
  )
  const aside = (
    <aside className="ofx-aside" aria-label={t('summaryLabel')}>
      <Side key={viewId} />
    </aside>
  )

  return (
    <StatsContext.Provider value={ctx}>
      <div className={`lib ofp ofx ${mobile ? 'lib--m ofx--m' : ''}`}>
        {mobile ? (
          <>
            {head}
            {tabs}
            <div className="ofx-scroll" ref={scrollRef}>
              {content}
              {aside}
            </div>
            {mobileNav}
          </>
        ) : (
          <>
            <section className="lib-main ofx-main">
              {head}
              {tabs}
              <div className="ofx-scroll" ref={scrollRef}>
                {content}
              </div>
            </section>
            {aside}
          </>
        )}
        {custom && (
          <CustomRange
            value={custom}
            onChange={(which, day) => setCustom((c) => c && { ...c, [which]: day })}
            onCancel={() => setCustom(null)}
            onApply={applyCustom}
          />
        )}
        {withdrawing && canWritePayouts && balances && (
          <WithdrawModal balances={balances} submit={submit} onFinished={withdrawalFinished} onClose={() => setWithdrawing(false)} />
        )}
        {toast}
      </div>
    </StatsContext.Provider>
  )
}
